<template>
  <div class="agreement-success" :class="{ 'agreement-success--eudora': brand === 'eudora' }">
    <div v-if="showFinalNotice" class="agreement-success__final">
      <div class="agreement-success__final-icon-wrap" aria-hidden="true">
        <div class="agreement-success__final-icon-circle">
          <i class="fa fa-check" />
        </div>
      </div>
      <h4 class="agreement-success__final-title">
        <span class="agreement-success__final-title-line">Tudo certo! Acompanhe seu acordo pela</span>
        <span class="agreement-success__final-title-line">página inicial quando quiser.</span>
      </h4>
      <div class="agreement-success__final-btn-wrap">
        <button
          class="agreement-success__final-btn"
          type="button"
          @click="$emit('return-to-dashboard')"
        >
          VER ACORDO
        </button>
      </div>
      <hr class="agreement-success__final-divider" />
      <p class="agreement-success__final-text">
        <span class="agreement-success__final-text-line">Em caso de dúvidas, acesse nossos</span>
        <span class="agreement-success__final-text-line">canais de atendimento:</span>
      </p>
      <p class="agreement-success__final-phone">0800 745 0000</p>
      <p class="agreement-success__final-link">regularizagrupoboticario.com.br</p>
    </div>

    <div v-else class="agreement-success__inner">
      <h6 class="agreement-success__title agreement-success__title--mobile-only">Resumo do seu acordo</h6>

      <div class="agreement-success__grid">
        <h6 class="agreement-success__section-label agreement-success__section-label--desktop-only">
          Resumo do acordo
        </h6>

        <p class="agreement-success__text agreement-success__payment-intro--desktop">
          {{ paymentIntroText }}
        </p>

        <div class="agreement-success__summary-col">
          <div class="summary-card">
            <div class="summary-card__brand">
              <span class="summary-card__brand-icon-slot" aria-hidden="true">
                <img :src="brandIcon" alt="" class="summary-card__brand-icon" />
              </span>
              <strong class="summary-card__brand-name">{{ brandName }}</strong>
            </div>

            <div class="summary-card__row">
              <span class="summary-card__label">Valor do acordo:</span>
              <strong class="summary-card__value">{{ agreementTotalValueFormatted }}</strong>
            </div>
            <div class="summary-card__row">
              <span class="summary-card__label">Plano:</span>
              <strong class="summary-card__value">{{ isAVista ? 'À vista' : 'Parcelado' }}</strong>
            </div>
            <div class="summary-card__row" v-if="!isAVista && hasEntry">
              <span class="summary-card__label">Entrada:</span>
              <strong class="summary-card__value">{{ entryValueFormatted }}</strong>
            </div>
            <div class="summary-card__row" v-if="!isAVista">
              <span class="summary-card__label">Parcelas:</span>
              <strong class="summary-card__value">{{ installmentsLabel }}</strong>
            </div>
            <div class="summary-card__row" v-else>
              <span class="summary-card__label">Valor do pagamento:</span>
              <strong class="summary-card__value">{{ paymentValueFormatted }}</strong>
            </div>
            <div class="summary-card__row">
              <span class="summary-card__label">{{ paymentMethodCode === 'L' ? 'Vencimento do acordo:' : 'Vencimento:' }}</span>
              <strong class="summary-card__value">{{ dueDateFormatted }}</strong>
            </div>
            <div class="summary-card__row">
              <span class="summary-card__label">Forma de pagamento:</span>
              <strong class="summary-card__value">{{ displayPaymentMethodLabel }}</strong>
            </div>

            <hr class="summary-card__divider" />
            <div class="summary-card__titles-row">
              <span class="summary-card__label">Título(s) negociados:</span>
              <span class="summary-card__titles-value">
                {{ isTitlesExpanded ? titlesText : truncated(titlesText, 20) }}
              </span>
              <button
                v-if="titlesText && titlesText.length > 20"
                class="summary-card__btn-expand"
                @click="isTitlesExpanded = !isTitlesExpanded"
              >
                <i :class="isTitlesExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
              </button>
            </div>
          </div>
        </div>

        <div class="agreement-success__payment-col">
          <p class="agreement-success__text agreement-success__payment-intro--mobile-only">
            {{ paymentIntroText }}
          </p>

          <template v-if="paymentMethodCode === 'L' && cardLink">
            <div class="action-box action-box--link" @click="openCardLink">
              <span class="action-box__text">{{ cardLink }}</span>
              <i class="fa fa-external-link action-box__icon" aria-hidden="true" />
            </div>
          </template>

          <template v-else-if="paymentMethodCode === 'P'">
            <div class="action-box" :class="{ 'action-box--copied': copyFeedbackVisible }" @click="copyPaymentCode('copy_pix', pixCode)">
              <span class="action-box__text">{{ pixCode }}</span>
              <i class="fa fa-clone action-box__icon" aria-hidden="true" />
              <span v-if="copyFeedbackVisible" class="action-box__copied-feedback">Código copiado</span>
            </div>
            <p class="agreement-success__qr-label">Escaneie o QR Code</p>
            <div class="agreement-success__qr-wrap">
              <qrcode-vue :value="pixCode" :size="200" level="H" />
            </div>
          </template>

          <template v-else>
            <div class="action-box" :class="{ 'action-box--copied': copyFeedbackVisible }" @click="copyPaymentCode('copy_digitable_line', digitableLine)">
              <span class="action-box__text">{{ digitableLine }}</span>
              <i class="fa fa-clone action-box__icon" aria-hidden="true" />
              <span v-if="copyFeedbackVisible" class="action-box__copied-feedback">Código copiado</span>
            </div>
          </template>

          <p v-if="!isAVista" class="agreement-success__next-installments">
            Os próximos boletos do seu acordo estarão disponíveis no site regularizagrupoboticario.com.br
          </p>
          <button
            v-if="hasBillPdf"
            type="button"
            class="agreement-success__download-bill"
            :disabled="downloadingBillPdf"
            @click="downloadBillPdf"
          >
            <i class="fa fa-download mr-2" aria-hidden="true" />
            {{ downloadingBillPdf ? 'BAIXANDO...' : 'BAIXAR BOLETO' }}
          </button>
        </div>
      </div>

      <div v-if="showWhatsappBoletoInvite" class="agreement-success__whatsapp-below">
        <div class="whatsapp-box">
          <p class="whatsapp-box__label">Deseja receber o boleto pelo WhatsApp?</p>
          <button class="whatsapp-box__button" type="button" @click="openWhatsappModal">
            <i class="fa fa-whatsapp mr-2" aria-hidden="true" />Quero receber
          </button>
        </div>
      </div>

      <div class="agreement-success__footer-actions">
        <button class="agreement-success__conclude" @click="onConcluir">CONCLUIR</button>
      </div>
    </div>

    <div v-if="showWhatsappModal" class="agreement-modal agreement-modal--backdrop" aria-modal="true" role="dialog">
      <div class="agreement-modal__card agreement-modal__card--form">
        <button type="button" class="agreement-modal__close" aria-label="Fechar" @click="closeWhatsappModal">
          <i class="fa fa-times" aria-hidden="true" />
        </button>
        <h3 class="agreement-modal__title agreement-modal__title--left">Boleto por WhatsApp</h3>

        <template v-if="hasStoredPhone">
          <p class="agreement-modal__hint">Receba pelo número abaixo</p>
          <label class="agreement-modal__phone-box">
            <input
              v-model="useSavedNumber"
              type="checkbox"
              class="agreement-modal__checkbox"
              @change="onUseSavedNumberChange"
            />
            <strong class="agreement-modal__phone-text">{{ storedPhoneDisplay }}</strong>
          </label>
          <p class="agreement-modal__info">
            <span class="agreement-modal__info-icon" aria-hidden="true">!</span>
            <span class="agreement-modal__info-text">Você receberá uma mensagem pelo WhatsApp com o seu boleto de pagamento.</span>
          </p>
        </template>

        <p class="agreement-modal__hint" :class="{ 'agreement-modal__hint--spaced': hasStoredPhone }">
          Ou informe outro número de WhatsApp
        </p>
        <input
          v-model="otherWhatsappPhone"
          type="tel"
          class="agreement-modal__input"
          placeholder="Ex: (41) 99999-9999"
          autocomplete="tel"
          @input="onOtherWhatsappInput"
        />

        <button
          type="button"
          class="agreement-modal__cta"
          :disabled="!canSubmitWhatsapp || whatsappSending"
          @click="submitWhatsapp"
        >
          {{ whatsappSending ? 'ENVIANDO…' : 'RECEBER BOLETO' }}
        </button>
      </div>
    </div>

    <div v-if="showWhatsappResultModal" class="agreement-modal agreement-modal--backdrop" aria-modal="true" role="dialog">
      <div class="agreement-modal__card agreement-modal__card--result">
        <button type="button" class="agreement-modal__close" aria-label="Fechar" @click="closeWhatsappResultModal">
          <i class="fa fa-times" aria-hidden="true" />
        </button>

        <template v-if="whatsappResultSuccess">
          <div class="agreement-modal__result-icon agreement-modal__result-icon--ok" aria-hidden="true">
            <i class="fa fa-check" />
          </div>
          <h3 class="agreement-modal__title agreement-modal__title--center">Boleto enviado com sucesso!</h3>
          <p class="agreement-modal__body agreement-modal__body--center">
            O boleto foi enviado para o número de WhatsApp informado
          </p>
          <button type="button" class="agreement-modal__cta agreement-modal__cta--block" @click="closeWhatsappResultModal">
            OK
          </button>
        </template>

        <template v-else>
          <div class="agreement-modal__result-icon agreement-modal__result-icon--err" aria-hidden="true">
            <i class="fa fa-exclamation" />
          </div>
          <h3 class="agreement-modal__title agreement-modal__title--center">Falha ao enviar boleto!</h3>
          <p class="agreement-modal__body agreement-modal__body--center">
            Ocorreu um erro ao enviar o boleto. Por favor, tente novamente.
          </p>
          <button type="button" class="agreement-modal__link-back" @click="closeWhatsappResultModal">Voltar</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatData } from '@/utils/helpers';
import QrcodeVue from 'qrcode.vue';
import flashMessages from '@/utils/flash-messages';

export default {
  name: 'AgreementSuccess',
  components: { QrcodeVue },
  props: {
    agreement: { type: Object, default: () => ({}) },
    prepareToSetAgreement: { type: Object, default: () => ({}) },
    selectedPaymentMethod: { type: String, default: null },
    brand: { type: String, default: 'boticario' },
  },
  mounted() {
    this.$emit('final-state-change', false);
  },
  data: () => ({
    isTitlesExpanded: false,
    showFinalNotice: false,
    showWhatsappModal: false,
    showWhatsappResultModal: false,
    whatsappResultSuccess: false,
    whatsappSending: false,
    useSavedNumber: true,
    otherWhatsappPhone: '',
    storedPhoneDigits: '',
    copyFeedbackVisible: false,
    copyFeedbackTimeoutId: null,
    downloadingBillPdf: false,
  }),
  computed: {
    brandName() {
      return this.brand === 'eudora' ? 'Eudora' : 'Boticário';
    },
    brandIcon() {
      return this.brand === 'eudora' ? '/img/eudora-icone-card.png' : '/img/boticario-icone-card.png';
    },
    titlesText() {
      return this.prepareToSetAgreement?.installments || '';
    },
    isAVista() {
      return Number(this.prepareToSetAgreement?.installmentNumber) <= 1;
    },
    hasEntry() {
      return Number(this.prepareToSetAgreement?.entryValue || 0) > 0;
    },
    entryValueFormatted() {
      if (!this.prepareToSetAgreement) return '';
      return this.formatFloatToReal(this.prepareToSetAgreement.entryValue);
    },
    paymentValueFormatted() {
      const v = this.prepareToSetAgreement?.installmentValue;
      return this.formatFloatToReal(v);
    },
    agreementTotalValueFormatted() {
      const p = this.prepareToSetAgreement || {};
      const installmentNumber = Number(p.installmentNumber);
      const installmentValue = Number(p.installmentValue);
      if (Number.isNaN(installmentValue)) return '—';
      if (!Number.isNaN(installmentNumber) && installmentNumber > 1) {
        return this.formatFloatToReal(installmentValue * installmentNumber);
      }
      return this.formatFloatToReal(installmentValue);
    },
    installmentsLabel() {
      if (!this.prepareToSetAgreement) return '';
      return `${this.prepareToSetAgreement.installmentNumber}x de ${this.formatFloatToReal(this.prepareToSetAgreement.installmentValue)}`;
    },
    dueDateFormatted() {
      return this.formatDate(this.agreement?.dueDate);
    },
    apiPaymentMethodCode() {
      const a = this.agreement || {};
      const raw = String(
        a.negotiationFormPg
        || a.paymentMethod
        || a.formaPagamento
        || a.paymentType
        || a.tipoPagamento
        || ''
      ).trim().toUpperCase();
      if (['P', 'L', 'B'].includes(raw)) return raw;
      if (raw === 'PIX') return 'P';
      if (raw === 'BOLETO') return 'B';
      if (raw.includes('CART')) return 'L';
      return '';
    },
    paymentMethodCode() {
      const sel = String(this.selectedPaymentMethod || '').trim().toUpperCase();
      if (['B', 'P', 'L'].includes(sel)) return sel;
      if (this.apiPaymentMethodCode) return this.apiPaymentMethodCode;
      if (this.digitableLine) return 'B';
      if (this.pixCode) return 'P';
      if (this.cardLink) return 'L';
      return 'B';
    },
    pixCode() {
      const a = this.agreement || {};
      return String(
        a.pixCopyAndPast
        || a.pixCopyPaste
        || a.pixCopiaECola
        || a.pixCopiaecola
        || a.pixCode
        || a.pix
        || ''
      ).trim();
    },
    digitableLine() {
      const a = this.agreement || {};
      return String(
        a.digitableLine
        || a.linhaDigitavel
        || a.linha_digitavel
        || a.barCodeLine
        || ''
      ).trim();
    },
    cardLink() {
      const a = this.agreement || {};
      return String(
        a.urlCard
        || a.linkCard
        || a.paymentLink
        || a.cardLink
        || ''
      ).trim();
    },
    apiPaymentMethodLabel() {
      if (!this.apiPaymentMethodCode) return '';
      if (this.apiPaymentMethodCode === 'L') return 'Cartão de Crédito';
      if (this.apiPaymentMethodCode === 'P') return 'PIX';
      return 'Boleto';
    },
    displayPaymentMethodLabel() {
      if (this.apiPaymentMethodLabel) return this.apiPaymentMethodLabel;
      const c = this.paymentMethodCode;
      if (c === 'P') return 'PIX';
      if (c === 'L') return 'Cartão de Crédito';
      if (c === 'B') return 'Boleto';
      return '—';
    },
    paymentIntroText() {
      if (this.paymentMethodCode === 'P') {
        return 'Copie a chave PIX ou escaneie o QR Code para realizar o pagamento.';
      }
      if (this.paymentMethodCode === 'L' && this.cardLink) {
        return 'Para efetuar o pagamento com cartão de crédito, acesse um ambiente seguro através do link abaixo:';
      }
      return 'Faça o pagamento com a linha digitável abaixo:';
    },
    showWhatsappBoletoInvite() {
      return ['B', 'P', 'L'].includes(this.paymentMethodCode);
    },
    whatsappFrom() {
      return this.brand === 'eudora' ? 'Eudora' : 'Boticario';
    },
    hasStoredPhone() {
      return this.storedPhoneDigits.length >= 10;
    },
    storedPhoneDisplay() {
      return this.formatPhoneBR(this.storedPhoneDigits);
    },
    canSubmitWhatsapp() {
      const other = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      if (other.length >= 10) return true;
      if (this.useSavedNumber && this.hasStoredPhone) return true;
      return false;
    },
    billPdfBase64() {
      const raw = String(this.agreement?.billPdf || '').trim();
      if (!raw) return '';
      return raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
    },
    pixPdfBase64() {
      const raw = String(this.agreement?.pixPdf || '').trim();
      if (!raw) return '';
      return raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
    },
    hasBillPdf() {
      return this.billPdfBase64.length > 0 || this.pixPdfBase64.length > 0;
    },
    agreementPdfBase64ForDownload() {
      if (this.paymentMethodCode === 'P') {
        return this.pixPdfBase64 || this.billPdfBase64;
      }
      return this.billPdfBase64 || this.pixPdfBase64;
    },
  },
  methods: {
    ...mapActions('negotiation', [
      'ActionNegotiationSendEmailOrWhatsapp',
      'ActionNegotiationLogPaymentAction',
    ]),
    formatFloatToReal(value) {
      const n = parseFloat(value);
      if (Number.isNaN(n)) return '—';
      return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatDate(date) {
      return formatData(date, 'YYYY-MM-DD', 'L');
    },
    async copiar(text) {
      if (!text) return false;
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.$emit('copied');
      return true;
    },
    async copiarComFeedback(text) {
      const copied = await this.copiar(text);
      if (!copied) return false;
      this.copyFeedbackVisible = true;
      if (this.copyFeedbackTimeoutId) clearTimeout(this.copyFeedbackTimeoutId);
      this.copyFeedbackTimeoutId = setTimeout(() => {
        this.copyFeedbackVisible = false;
        this.copyFeedbackTimeoutId = null;
      }, 3500);
      return true;
    },
    logPaymentAction(action, paymentValue) {
      if (!['boticario', 'eudora'].includes(this.brand)) return;
      this.ActionNegotiationLogPaymentAction({
        brand: this.brand,
        action,
        agreement: this.agreement,
        prepareToSetAgreement: this.prepareToSetAgreement,
        paymentMethod: this.paymentMethodCode,
        paymentValue,
      }).catch((err) => {
        console.warn('[agreement-success] Log ação de pagamento Boticário ignorado:', err);
      });
    },
    async copyPaymentCode(action, text) {
      const copied = await this.copiarComFeedback(text);
      if (copied) this.logPaymentAction(action, text);
    },
    base64ToPdfBlob(base64) {
      if (!base64) throw new Error('Boleto indisponível para download.');
      if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
        throw new Error('Formato do boleto inválido.');
      }
      let binary = '';
      try {
        binary = window.atob(base64);
      } catch (e) {
        throw new Error('Não foi possível decodificar o boleto.');
      }
      if (!binary.startsWith('%PDF-')) {
        throw new Error('Arquivo de boleto inválido.');
      }
      const chunkSize = 1024;
      const byteArrays = [];
      for (let offset = 0; offset < binary.length; offset += chunkSize) {
        const slice = binary.slice(offset, offset + chunkSize);
        const bytes = new Array(slice.length);
        for (let i = 0; i < slice.length; i += 1) bytes[i] = slice.charCodeAt(i);
        byteArrays.push(new Uint8Array(bytes));
      }
      return new Blob(byteArrays, { type: 'application/pdf' });
    },
    downloadBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    async downloadBillPdf() {
      if (this.downloadingBillPdf) return;
      this.downloadingBillPdf = true;
      try {
        const base64 = this.agreementPdfBase64ForDownload;
        const blob = this.base64ToPdfBlob(base64);
        const agreementId = String(
          this.agreement?.agreementId
          || this.prepareToSetAgreement?.agreementId
          || ''
        ).trim();
        const filename = agreementId ? `boleto-acordo-${agreementId}.pdf` : 'boleto-acordo.pdf';
        this.downloadBlob(blob, filename);
        flashMessages.success('Boleto baixado com sucesso.', 'Sucesso', 1800);
        if (this.brand === 'boticario') {
          this.logPaymentAction('download_boleto', this.digitableLine || agreementId || filename);
        }
      } catch (err) {
        flashMessages.error(err.message || 'Não foi possível baixar o boleto.', 'Erro', 3000);
      } finally {
        this.downloadingBillPdf = false;
      }
    },
    openCardLink() {
      if (!this.cardLink) return;
      this.logPaymentAction('click_card_link', this.cardLink);
      window.open(this.cardLink, '_blank', 'noopener,noreferrer');
    },
    truncated(text, length) {
      if (!text) return '';
      return text.length > length ? `${text.substring(0, length)}...` : text;
    },
    onConcluir() {
      this.showFinalNotice = true;
      this.$emit('final-state-change', true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    refreshStoredWhatsappPhone() {
      let u = null;
      try {
        u = JSON.parse(localStorage.getItem('user') || 'null');
      } catch (e) {
        u = null;
      }
      const raw = u?.PhoneNumber != null ? u.PhoneNumber : u?.MobileNumber;
      this.storedPhoneDigits = String(raw || '').replace(/\D/g, '');
    },
    formatPhoneBR(digits) {
      const d = String(digits || '').replace(/\D/g, '');
      if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)} ${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return d || '—';
    },
    onUseSavedNumberChange() {
      if (this.useSavedNumber) this.otherWhatsappPhone = '';
    },
    onOtherWhatsappInput() {
      const d = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      if (d.length > 0) this.useSavedNumber = false;
    },
    openWhatsappModal() {
      this.refreshStoredWhatsappPhone();
      this.otherWhatsappPhone = '';
      this.useSavedNumber = this.storedPhoneDigits.length >= 10;
      this.showWhatsappModal = true;
    },
    closeWhatsappModal() {
      if (this.whatsappSending) return;
      this.showWhatsappModal = false;
    },
    closeWhatsappResultModal() {
      this.showWhatsappResultModal = false;
    },
    async submitWhatsapp() {
      if (!this.canSubmitWhatsapp || this.whatsappSending) return;
      const other = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      const phone = other.length >= 10 ? other : (this.useSavedNumber ? this.storedPhoneDigits : '');
      if (phone.length < 10) return;

      this.whatsappSending = true;
      try {
        await this.ActionNegotiationSendEmailOrWhatsapp({
          sendEmail: false,
          sendWhatsapp: true,
          from: this.whatsappFrom,
          phoneNumber: phone,
        });
        this.showWhatsappModal = false;
        this.whatsappResultSuccess = true;
        this.showWhatsappResultModal = true;
      } catch (err) {
        this.showWhatsappModal = false;
        this.whatsappResultSuccess = false;
        this.showWhatsappResultModal = true;
      } finally {
        this.whatsappSending = false;
      }
    },
  },
  beforeDestroy() {
    if (this.copyFeedbackTimeoutId) clearTimeout(this.copyFeedbackTimeoutId);
  },
};
</script>

<style scoped>
.agreement-success {
  padding-top: 8px;
  padding-bottom: 32px;
  min-height: calc(100dvh - 140px);
}

.agreement-success__inner {
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
}

.agreement-success__grid {
  display: block;
}

.agreement-success__summary-col,
.agreement-success__payment-col {
  min-width: 0;
}

.agreement-success__section-label--desktop-only {
  display: none;
}

.agreement-success__payment-intro--desktop {
  display: none;
}

.agreement-success__title {
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #333438;
  margin: 12px 0 16px;
}

.agreement-success__whatsapp-below {
  margin-top: 14px;
}

.agreement-success__whatsapp-below .whatsapp-box {
  margin-top: 0;
}

.agreement-success__footer-actions {
  width: 100%;
  margin-top: 22px;
}

.agreement-success__footer-actions .agreement-success__conclude {
  margin-top: 0;
}

.summary-card {
  border: none;
  border-top: 4px solid #06a472;
  border-radius: 20px;
  padding: 16px 16px 12px;
  background-color: #fff;
  box-shadow: var(--negotiation-card-shadow, 0 2px 12px rgba(0, 0, 0, 0.08));
  margin-bottom: 16px;
}

.summary-card__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.summary-card__brand-icon-slot {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.summary-card__brand-icon {
  width: 100%;
  height: 100%;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.summary-card__brand-name {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #333438;
}

.summary-card__row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
}

.summary-card__label {
  color: #555;
  white-space: nowrap;
}

.summary-card__value {
  font-weight: 700;
  color: #333438;
}

.summary-card__divider {
  margin: 6px 0;
  border-color: #e2e2e2;
}

.summary-card__titles-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
}

.summary-card__titles-value {
  flex: 1;
  min-width: 0;
  color: #333438;
  line-height: 1.4;
  word-break: break-word;
}

.summary-card__btn-expand {
  border: none;
  background: transparent;
  color: #176e53;
  padding: 0 2px;
  display: inline-flex;
  align-items: center;
}
.summary-card__btn-expand:focus { outline: none; }

.agreement-success__text {
  font-family: 'Inter', sans-serif;
  font-size: 0.98rem;
  color: #333438;
  line-height: 1.25;
  margin: 12px 0 10px;
}

.action-box {
  width: 100%;
  border: 1px solid #a4a4a4;
  border-radius: 8px;
  background: #ececec;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
}

.action-box__text {
  flex: 1;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  color: #454545;
  line-height: 1.35;
  word-break: break-all;
}

.action-box__icon {
  color: #505050;
  font-size: 1rem;
}

.action-box--copied {
  background: #e3e3e3;
}

.action-box__copied-feedback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(236, 236, 236, 0.58);
  backdrop-filter: blur(1.8px);
  -webkit-backdrop-filter: blur(1.8px);
  color: #2f2f2f;
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  pointer-events: none;
}

.action-box--link .action-box__text {
  word-break: break-word;
}

.agreement-success__qr-label {
  margin: 16px 0 8px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  color: #555;
}

.agreement-success__qr-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
}

.agreement-success__next-installments {
  margin: 10px 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.84rem;
  color: #555;
  line-height: 1.4;
  font-style: italic;
}

.agreement-success__download-bill {
  width: 100%;
  margin-top: 12px;
  border: 1px solid #06a472;
  border-radius: 8px;
  background: #fff;
  color: #06a472;
  min-height: 44px;
  font-family: 'Inter', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.agreement-success__download-bill:hover:not(:disabled) {
  background: rgba(6, 164, 114, 0.08);
}

.agreement-success__download-bill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


.whatsapp-box {
  margin-top: 14px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 12px;
}

.whatsapp-box__label {
  margin: 0 0 10px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  color: #555;
}

.whatsapp-box__button {
  width: 100%;
  max-width: 220px;
  display: block;
  margin: 0 auto;
  background: #1d9473;
  color: #fff;
  border: none;
  border-radius: 8px;
  min-height: 36px;
  font-family: 'Inter', sans-serif;
}

.agreement-success__conclude {
  width: 100%;
  border: none;
  border-radius: 8px;
  background: #1a7b5a;
  color: #fff;
  min-height: 48px;
  font-family: 'Inter', sans-serif;
  font-size: 1.12rem;
  font-weight: 500;
  letter-spacing: 0.3px;
}

@media (min-width: 768px) {
  .agreement-success__inner {
    max-width: 100%;
  }

  .agreement-success__title--mobile-only {
    display: none;
  }

  .agreement-success__section-label--desktop-only {
    display: block;
    grid-column: 1;
    grid-row: 1;
    margin: 0;
    align-self: baseline;
    font-family: 'Inter', sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    color: #333438;
  }

  .agreement-success__payment-intro--desktop {
    display: block;
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    align-self: baseline;
  }

  .agreement-success__payment-intro--mobile-only {
    display: none;
  }

  .agreement-success__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 32px;
    row-gap: 14px;
    align-items: start;
    margin-bottom: 32px;
  }

  .agreement-success__summary-col {
    grid-column: 1;
    grid-row: 2;
  }

  .agreement-success__payment-col {
    grid-column: 2;
    grid-row: 2;
  }

  .agreement-success__summary-col .summary-card {
    margin-bottom: 0;
  }

  .agreement-success__whatsapp-below {
    margin-top: 0;
    margin-bottom: 28px;
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }

  .agreement-success__footer-actions {
    max-width: 420px;
    margin-left: auto;
    margin-right: auto;
    margin-top: 0;
    padding-bottom: 8px;
  }
}

.agreement-success__final {
  --agreement-final-accent: #15795b;
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
  text-align: center;
  padding-top: 44px;
  box-sizing: border-box;
}

.agreement-success__final-icon-wrap {
  display: flex;
  justify-content: center;
}

.agreement-success__final-icon-circle {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  border: 6px solid var(--agreement-final-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--agreement-final-accent);
  font-size: 2.2rem;
}

.agreement-success__final-title {
  margin: 24px auto 20px;
  max-width: 100%;
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  line-height: 1.32;
  font-weight: 700;
  color: #333438;
}

.agreement-success__final-title-line {
  display: block;
}

.agreement-success__final-btn-wrap {
  display: flex;
  justify-content: center;
  width: 100%;
}

.agreement-success__final-btn {
  width: 100%;
  max-width: 300px;
  border: none;
  border-radius: 8px;
  background: var(--agreement-final-accent);
  color: #fff;
  min-height: 50px;
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  cursor: pointer;
}

.agreement-success__final-divider {
  margin: 24px auto 22px;
  border: none;
  border-top: 1px solid #e0e0e0;
  max-width: 100%;
}

.agreement-success__final-text {
  margin: 0 auto 16px;
  max-width: 280px;
  font-family: 'Inter', sans-serif;
  font-size: 0.92rem;
  line-height: 1.45;
  color: #666;
  font-weight: 400;
}

.agreement-success__final-text-line {
  display: block;
}

.agreement-success__final-phone,
.agreement-success__final-link {
  margin: 0 0 4px;
  font-family: 'Inter', sans-serif;
  font-size: 1.05rem;
  line-height: 1.35;
  font-weight: 700;
  color: var(--agreement-final-accent);
}

@media (min-width: 768px) {
  .agreement-success__final {
    max-width: 540px;
  }

  .agreement-success__final-title {
    font-size: 1.55rem;
    line-height: 1.3;
    margin-bottom: 22px;
  }

  .agreement-success__final-title-line:first-of-type {
    white-space: nowrap;
  }

  .agreement-success__final-btn {
    width: 62%;
    max-width: 300px;
    min-width: 240px;
  }

  .agreement-success__final-divider {
    max-width: 380px;
  }

  .agreement-success__final-text {
    max-width: 300px;
  }
}

.agreement-success__final-footer {
  margin-top: 36px;
  border-top: 1px solid #e6e7e9;
  padding-top: 14px;
}

.agreement-success__final-footer-logo {
  width: 132px;
  object-fit: contain;
}

.agreement-modal--backdrop {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.agreement-modal__card {
  position: relative;
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 22px 20px 20px;
  box-sizing: border-box;
}

.agreement-modal__card--result {
  padding-top: 28px;
  text-align: center;
}

.agreement-modal__close {
  position: absolute;
  top: 12px;
  right: 14px;
  border: none;
  background: transparent;
  color: #888;
  font-size: 1.25rem;
  line-height: 1;
  padding: 6px;
  cursor: pointer;
}

.agreement-modal__close:focus {
  outline: none;
}

.agreement-modal__title {
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #333438;
  margin: 0 0 16px;
  padding-right: 28px;
}

.agreement-modal__title--left {
  text-align: left;
}

.agreement-modal__title--center {
  text-align: center;
  padding-right: 0;
  margin-bottom: 12px;
}

.agreement-modal__hint {
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  color: #666;
  margin: 0 0 8px;
  text-align: left;
}

.agreement-modal__hint--spaced {
  margin-top: 18px;
}

.agreement-modal__phone-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ececec;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  cursor: pointer;
  text-align: left;
}

.agreement-modal__checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.agreement-modal__phone-text {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: #333438;
}

.agreement-modal__info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #555;
  margin: 0 0 4px;
  text-align: left;
}

.agreement-modal__info-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #06a472;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-top: 1px;
}

.agreement-modal__info-text {
  font-style: italic;
}

.agreement-modal__input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c8c8c8;
  border-radius: 8px;
  padding: 12px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 18px;
}

.agreement-modal__input::placeholder {
  color: #aaa;
}

.agreement-modal__cta {
  width: 100%;
  border: none;
  border-radius: 10px;
  background: #06a472;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  min-height: 48px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.agreement-modal__cta:hover:not(:disabled) {
  background: #05966a;
}

.agreement-modal__cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.agreement-modal__cta--block {
  margin-top: 8px;
  background: #15795b;
}

.agreement-modal__cta--block:hover:not(:disabled) {
  background: #125c44;
}

.agreement-modal__result-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #fff;
}

.agreement-modal__result-icon--ok {
  background: #06a472;
}

.agreement-modal__result-icon--err {
  background: #b3261e;
}

.agreement-modal__body {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.45;
  margin: 0 0 20px;
}

.agreement-modal__body--center {
  text-align: center;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
}

.agreement-modal__link-back {
  border: none;
  background: none;
  color: #15795b;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 8px;
}

.agreement-success--eudora .summary-card {
  border-top-color: #613a53;
}
.agreement-success--eudora .summary-card__btn-expand {
  color: #613a53;
}
.agreement-success--eudora .agreement-success__conclude {
  background: #613a53;
}
.agreement-success--eudora .agreement-success__download-bill {
  border-color: #613a53;
  color: #613a53;
}
.agreement-success--eudora .agreement-success__download-bill:hover:not(:disabled) {
  background: rgba(97, 58, 83, 0.08);
}
.agreement-success--eudora .agreement-success__final {
  --agreement-final-accent: #613a53;
}
.agreement-success--eudora .agreement-modal__info-icon {
  background: #613a53;
}
.agreement-success--eudora .agreement-modal__result-icon--ok {
  background: #613a53;
}
.agreement-success--eudora .agreement-modal__cta:not(.agreement-modal__cta--block) {
  background: #613a53;
}

.agreement-success--eudora .agreement-modal__cta:not(.agreement-modal__cta--block):hover:not(:disabled) {
  background: #4d2e42;
}

.agreement-success--eudora .agreement-modal__cta--block {
  background: #613a53;
}

.agreement-success--eudora .agreement-modal__cta--block:hover:not(:disabled) {
  background: #4d2e42;
}
.agreement-success--eudora .agreement-modal__link-back {
  color: #613a53;
}

</style>
